// Copyright © 2025–2026 Maestro Design & Management AB. Proprietary and confidential; see LICENSE.
// Selection-rectangle helpers shared by Tomma ytor and Space syntax.

export const M_PER_DEG_LAT = 111320;

// Selection rectangle size in km2 (equirectangular - fine at city scale)
export const boundsAreaKm2 = (b) => {
  const latMid = (b.south + b.north) / 2;
  const w = (b.east - b.west) * 111.32 * Math.cos((latMid * Math.PI) / 180);
  const h = (b.north - b.south) * 111.32;
  return w * h;
};

// The selection box is pinned to the SCREEN, not the map: it is stored as
// metre dimensions + offset from the map centre, so panning slides the map
// underneath it and zooming keeps its ground size (screen size adapts).
export const boundsToBox = (b, center) => {
  const midLat = (b.south + b.north) / 2;
  const midLng = (b.west + b.east) / 2;
  return {
    wM: (b.east - b.west) * M_PER_DEG_LAT * Math.cos((midLat * Math.PI) / 180),
    hM: (b.north - b.south) * M_PER_DEG_LAT,
    dLat: midLat - center.lat,
    dLng: midLng - center.lng,
  };
};

export const boxToBounds = (box, center) => {
  const midLat = center.lat + box.dLat;
  const midLng = center.lng + box.dLng;
  const halfLat = box.hM / M_PER_DEG_LAT / 2;
  const halfLng = box.wM / (M_PER_DEG_LAT * Math.cos((midLat * Math.PI) / 180)) / 2;
  return {
    south: midLat - halfLat, north: midLat + halfLat,
    west: midLng - halfLng, east: midLng + halfLng,
  };
};

// "W × H m" ground size of a bounds object
export const dimsLabelOf = (b) => (b
  ? `${Math.round((b.east - b.west) * M_PER_DEG_LAT * Math.cos(((b.south + b.north) / 2) * Math.PI / 180))} × ${
    Math.round((b.north - b.south) * M_PER_DEG_LAT)} m`
  : '');

// Neighborhood/district name for the analysed point, e.g. "Äppelviken" -
// used to name saved runs and exported PNGs. OSM Nominatim: free, no key, and
// Swedish stadsdelar are well mapped (Googles Geocoding API is not enabled for
// our key). Best-effort: null on any failure, and after 4 s - an analysis
// waits for this before it starts.
export const lookupAreaName = async (lat, lng) => {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), 4000);
  try {
    const resp = await fetch(
      `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}` +
      '&format=jsonv2&zoom=14&accept-language=sv', { signal: ctrl.signal });
    const addr = (await resp.json()).address || {};
    const name = addr.neighbourhood || addr.suburb || addr.town
      || addr.village || addr.city || addr.municipality || null;
    // Keep letters/digits/space/dash only - this becomes a filename.
    return name ? (name.replace(/[^\p{L}\p{N} -]/gu, '').trim() || null) : null;
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
};
